import { Box, CopyToClipboardButton, Form, Stack, TwoColumnLayout, Typography } from '@garner-health/components-common';
import type { MapMarker } from '@garner-health/components-map-v3';
import { SR } from '@garner-health/lib-ui-content-management';
import { useFlags } from '@garner-health/lib-ui-feature-flags';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import constants from 'app-constants';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { analytics, Event } from '~/analytics';
import { ProviderResultItem, ProvidersResponseSetType } from '~/clients';
import { WrappedGarnerMap } from '~/components/common/garner-map-wrapper';
import { ProvidersTable } from '~/components/provider-search/providers-table';
import { SearchFormData } from '~/components/provider-search/search-form';
import { SortByForm } from '~/components/provider-search/sort-by-form';
import { TieredNetworkCallout } from '~/components/provider-search/tiered-network-callout';
import { useMapAssets } from '~/hooks/use-map-assets';
import { useSortedResults } from '~/hooks/use-sorted-results';
import { routes } from '~/router/routes';
import { formatProviderInfoForClipboard, providerKeyResolver } from '~/util';

import { MAX_PROVIDER_ROW_SELECTIONS } from './common';
import { useSelectedProviderIds } from './use-provider-selections';

function useMarkers(providers: ProviderResultItem[]) {
  const [markers, setMarkers] = useState<MapMarker[]>([]);

  useEffect(() => {
    const newMarkers: MapMarker[] = providers.map(provider => ({
      type: provider.isTopProvider ? 'primary' : 'secondary',
      key: providerKeyResolver(provider),
      lat: provider.location.position.lat,
      lng: provider.location.position.lng,
    }));

    setMarkers(newMarkers);
  }, [providers]);

  return markers;
}

function useNavigateToProviderDetails({ formData }: { formData: SearchFormData }) {
  const navigate = useNavigate();
  const networkId = formData.network.value;

  return (item: ProviderResultItem) => {
    let metricIds: string[] = [];
    if (formData.careGoal) {
      metricIds = formData.careGoal.metricIds.filter(Boolean);
    } else if (item.type === 'physician') {
      metricIds = item.metrics?.filter(metric => metric.id.includes(item.specialty)).map(metric => metric.id) ?? [];
    }

    navigate(
      routes.providerDetails({
        id: item.id,
        locationId: item.location.id,
        specialty: item.specialty,
        networkId,
        metricIds,
      }),
    );
  };
}

type SearchResultsProps = {
  providers: ProviderResultItem[];
  responseSetType: ProvidersResponseSetType;
  formData: SearchFormData;
  isTieredNetwork?: boolean;
};

function SelectionsText({ selectedRows }: { selectedRows: number }) {
  if (!selectedRows) return;
  if (selectedRows === MAX_PROVIDER_ROW_SELECTIONS) {
    return (
      <SR
        package="search"
        id="maximumProvidersSelectedLabel"
        interpolate={{
          numberOfProviders: () => `${selectedRows}`,
          maximum: () => `${MAX_PROVIDER_ROW_SELECTIONS}`,
        }}
      />
    );
  }
  return (
    <SR package="search" id="providersSelectedLabel" interpolate={{ numberOfProviders: () => `${selectedRows}` }} />
  );
}

/**
 * Wraps Providers list in a form with a SelectInput that is used to sort items in the providers list
 */
export const SearchResults = ({ providers, responseSetType, formData, isTieredNetwork }: SearchResultsProps) => {
  const { showProviderAffiliations } = useFlags();
  const { sortMethod, setSortMethod, sortedProviders } = useSortedResults(providers, responseSetType);
  const markers = useMarkers(sortedProviders);
  const onNavigate = useNavigateToProviderDetails({ formData });
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const form = useForm();
  // Hold the map until manifest resolution settles so MapLibre isn't handed the
  // fallback URL mid-load (which fetches the legacy tile before the hashed one).
  // `isLoading` is only true on the first map mount of a session.
  const { tileUrl, spriteBaseUrl, isLoading } = useMapAssets();

  const { selectedProviderInfo, isProviderResultSelected } = useSelectedProviderIds(form);

  return (
    <TwoColumnLayout containScroll>
      <TwoColumnLayout.Column1>
        <Box padding="sm">
          <Stack direction="row" justifyContent="space-between">
            <Stack.Group>
              <Stack direction="row">
                {!!selectedProviderInfo.length && (
                  <Stack direction="row" spacing="md">
                    <Typography variant="body1" color="grey.800">
                      <SelectionsText selectedRows={selectedProviderInfo.length} />
                    </Typography>

                    <CopyToClipboardButton
                      icon={<ContentCopyOutlinedIcon />}
                      iconPosition="start"
                      value={formatProviderInfoForClipboard(
                        sortedProviders.filter(isProviderResultSelected),
                        showProviderAffiliations,
                      )}
                      onClick={() => {
                        analytics.track(Event.COPY_CARD_TO_CLIPBOARD);
                      }}
                    >
                      <SR package="search" id="copySelectedProviderInfoToClipboardButtonText" />
                    </CopyToClipboardButton>
                  </Stack>
                )}
              </Stack>
            </Stack.Group>
            <SortByForm searchType={responseSetType} sortMethod={sortMethod} onChange={setSortMethod} />
          </Stack>
          <Form form={form}>
            <ProvidersTable
              searchType={responseSetType}
              providers={sortedProviders}
              configureRow={(item, key) => ({
                onMouseEnter: () => setSelectedKey(key),
                onMouseLeave: () => setSelectedKey(undefined),
                onClick: () => onNavigate(item),
              })}
              selectedKey={selectedKey}
            />
          </Form>

          {!!isTieredNetwork && <TieredNetworkCallout />}
        </Box>
      </TwoColumnLayout.Column1>

      <TwoColumnLayout.Column2>
        {!isLoading && (
          <WrappedGarnerMap
            tileUrl={tileUrl}
            spriteBaseUrl={spriteBaseUrl}
            assetBaseUrl={constants.map.assetBaseUrl}
            interactive
            center={formData.location.position}
            markers={markers}
            selectedKeys={selectedKey ? [selectedKey] : undefined}
            renderMarkerHandlers={marker => ({
              onMouseEnter: () => setSelectedKey(marker.key),
              onMouseLeave: () => setSelectedKey(undefined),
            })}
          />
        )}
      </TwoColumnLayout.Column2>
    </TwoColumnLayout>
  );
};
